<template>
    <div class="container-fluid">
        <v-title :lineage="[{'Automations':'automations'}]">
            Data Exports
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages['add-data-export'].isAllowed"
                    :to="{name: 'add-data-export'}"
                    class="btn btn-link">Add Data Export<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
            </div>
        </v-title>
        <div class="grid flex-flow-column">
            <div class="grid-item">
                <grid
                    ref="grid"
                    :columns="gridColumns"
                    :provider="provider"
                    :options="gridOptions"
                    :tabs="gridTabs"
                    :filter-set="gridFilterSet"
                    @completed="stopLoadingPage">
                    <v-no-content slot="no-content">
                        <span slot="title">It seems that you have no data exports</span>
                        <span slot="description">Click on the button below to add your first data export.</span>
                        <div slot="actions">
                            <router-link
                                v-if="$auth.$pages['add-data-export'].isAllowed"
                                :to="{name: 'add-data-export'}"
                                class="btn btn-link">Add Data Export<v-icon class="margin-left-xs" icon="arrow-right"/>
                            </router-link>
                        </div>
                    </v-no-content>
                </grid>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {vIcon} from '@/components/form';
    import vNoContent from '@/components/v-no-content';
    import Grid from '@/components/grid/grid';
    import _ from '@/common/helpers';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import {DataExportModel} from '@/common/models/data-export-model';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import TabsConfiguration from '@/components/grid/tabs/tabs-configuration';
    import dataExportFilters from '@/common/query/filter/data-export-filters';
    import createColumns from './data-exports-columns';
    import FilterSet from '@/components/query/filter-set';

    export default {
        name: 'data-exports',
        components: {
            vTitle, Grid, vNoContent, vIcon,
        },
        mixins: [LoadStateMixin],
        data() {
            return {
                gridColumns: createColumns({
                    onRowDownload: this.onRowDownload,
                    onRowEdit: this.onRowEdit,
                    onRowDelete: this.onRowDelete,
                    checkActionIsEnabled: this.checkActionIsEnabled,
                }),
                gridOptions: {
                    defaultSort: {scheduledTime: 'desc'},
                },
                gridTabs: new TabsConfiguration({
                    id: 'data-exports',
                    store: this.$store,
                    systemTabs: [
                        {
                            label: 'All',
                        },
                        {
                            label: 'My Exports',
                            filter: [['/user/email', [this.$store.getters['user/email']]]],
                        },
                        {
                            label: 'Pending',
                            filter: [['status', [DataExportModel.Statuses.pending]]],
                        },
                    ],
                    version: 1,
                    filtersVersion: dataExportFilters.version,
                }),
                gridFilterSet: new FilterSet({store: this.$store, ...dataExportFilters}),
            };
        },
        methods: {
            provider(params) {
                return this.$api(({dataExports}) => dataExports.getAll({
                    ...params,
                    expand: 'user,file',
                }));
            },
            checkActionIsEnabled({name, rowData}) {
                const {isCompleted, isPending} = new DataExportModel(rowData.fields);
                const map = {
                    download: () => isCompleted && !!this.getRowDownloadLink({rowData}),
                    edit: () => isPending,
                    delete: () => isPending || isCompleted,
                };
                return map[name]();
            },
            getRowDownloadLink({rowData}) {
                // eslint-disable-next-line no-underscore-dangle
                return DataExportModel.getSignedLink(rowData.fields._links);
            },
            async onRowDownload({rowData}) {
                // TODO implement with preloader
                const {fields} = rowData;
                try {
                    const exportData = await this.$api(({dataExports}) => dataExports.get({id: fields.id}));
                    const link = this.getRowDownloadLink({rowData: exportData});
                    window.open(link);
                } catch (error) {
                    this.$toast.fromError(error);
                }
            },
            onRowEdit({rowData}) {
                this.$router.push({name: 'update-data-export', params: {id: rowData.fields.id}});
            },
            async onRowDelete({rowData, grid}) {
                const model = new DataExportModel(rowData.fields);
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Delete ${_.unKebabCase(model.name)}`,
                    content: `Are you sure you want to delete this export? This cannot be undone.`,
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    try {
                        grid.startLoading();
                        await this.$api(({dataExports}) => dataExports.delete({
                            id: rowData.fields.id,
                        }));
                        grid.fetchData();
                        this.$toast.positive('Export item successfully deleted');
                    } catch (error) {
                        this.$toast.fromError(error);
                        grid.stopLoading();
                    }
                }
            },
        },
    };
</script>
